/* eslint-disable */
import { CallRoomController, HoldStatus } from "./CallRoomControllerModel";
import { CallRoomModel } from "./CallRoomModel";
import { CallSessionModel } from "./CallSessionModel";
import { OutboundCallController, OutboundCallState } from "./OutboundCallController";
import store from "@/store";

/**
 * Adapter class that wraps OutboundCallController to implement CallRoomController interface
 * This allows outbound calls to be displayed and controlled using the same UI components as inbound calls
 */
export class OutboundCallControllerAdapter implements CallRoomController {
  private outboundController: OutboundCallController;
  private _onMute = false;
  private _holdStatus = HoldStatus.OFF;
  private _isTransferring = false;
  private _isExpanded = true;

  constructor(outboundController: OutboundCallController) {
    this.outboundController = outboundController;
  }

  /** The call room model containing room details */
  get room(): CallRoomModel {
    // Handle both healthy controllers and corrupted controllers from localStorage deserialization
    if (typeof this.outboundController.getRoom === 'function') {
      return this.outboundController.getRoom();
    } else {
      // Fallback for corrupted controllers: extract room data directly from the object
      return (this.outboundController as any).room;
    }
  }

  /** The call session model containing session information */
  get session(): CallSessionModel {
    // Handle both healthy controllers and corrupted controllers from localStorage deserialization
    if (typeof this.outboundController.getSession === 'function') {
      return this.outboundController.getSession();
    } else {
      // Fallback for corrupted controllers: extract session data directly from the object
      return (this.outboundController as any).session;
    }
  }

  /** Indicates if the audio is muted */
  get onMute(): boolean {
    return this.outboundController.isMuted();
  }

  /** Current hold status of the call */
  get holdStatus(): HoldStatus {
    // Sync with actual controller hold state if not in loading state
    if (this._holdStatus !== HoldStatus.LOADING) {
      const isActuallyOnHold = this.outboundController.isHeld();
      this._holdStatus = isActuallyOnHold ? HoldStatus.ON : HoldStatus.OFF;
    }
    return this._holdStatus;
  }

  /** Indicates if call transfer is in progress */
  get isTransferring(): boolean {
    return this._isTransferring;
  }

  /** Indicates if the call UI is expanded */
  get isExpanded(): boolean {
    return this._isExpanded;
  }

  /** Connects to the call room - for outbound calls, this is already done during initiation */
  async connectToRoom(): Promise<CallRoomModel | Error> {
    // For outbound calls, connection is handled during call initiation
    // This method is mainly for interface compatibility
    if (this.outboundController.isCallActive()) {
      return this.room;
    }
    return new Error("Outbound call is not active");
  }

  /** Toggles audio mute state */
  toggleAudioMute(): void {
    this.outboundController.toggleMute();
    this._onMute = this.outboundController.isMuted();
  }

  /** Toggles call hold state using OutboundCallController hold functionality */
  async toggleHoldState(): Promise<CallRoomModel | Error> {
    const previousState = this._holdStatus;
    this._holdStatus = HoldStatus.LOADING;

    try {
      // Use the real hold functionality from OutboundCallController
      const isNowOnHold = await this.outboundController.toggleHold();

      // Update hold status based on the actual result
      this._holdStatus = isNowOnHold ? HoldStatus.ON : HoldStatus.OFF;

      return this.room;
    } catch (error) {
      // Restore previous state on error
      this._holdStatus = previousState;
      throw new Error(`Hold operation failed: ${error}`);
    }
  }

  /** Initiates call transfer - Not applicable for outbound calls */
  initiateTransfer(): void {
    // Transfer functionality is typically not available for outbound calls
    // This is a no-op for compatibility
    console.warn("Transfer is not supported for outbound calls");
  }

  /** Disconnects from the call room */
  async disconnectFromRoom(): Promise<CallRoomModel | { err: string }> {
    try {
      // Handle both healthy controllers and corrupted controllers from localStorage deserialization
      if (typeof this.outboundController.endCall === 'function') {
        await this.outboundController.endCall();
      } else {
        // For corrupted controllers, implement complete endCall logic from OutboundCallController
        console.warn("Corrupted controller detected, executing complete endCall logic in adapter");
        await this.endCallForCorruptedController();
      }
      return this.room;
    } catch (error) {
      return { err: `Failed to end outbound call: ${error}` };
    }
  }

  /** 
   * Complete endCall implementation for corrupted controllers
   * This replicates the full endCall logic from OutboundCallController
   */
  private async endCallForCorruptedController(): Promise<void> {
    try {
      console.log("endCall executed in adapter for corrupted controller");
      
      // Set state to ENDING if possible
      const controllerData = this.outboundController as any;
      if (controllerData.currentState !== undefined) {
        controllerData.currentState = OutboundCallState.ENDING;
      }

      // Get session data from corrupted controller
      const sessionData = this.session;
      if (!sessionData || !sessionData.roomId) {
        throw new Error("No session data available for termination");
      }

      // Import required dependencies dynamically
      const { InboundCallServiceClient } = await import("@/serviceClients/InboundCallServiceClient");
      const { RefreshableRequestHelper } = await import("@/helpers/RefreshableRequestHelper");
      const { isKokattoErrorResponse } = await import("@/serviceClients/responses/KokattoErrorResponse");

      // Create service client
      const inboundCallServiceClient = new InboundCallServiceClient({
        token: store.getters.getKokattoTokenAccess,
      });

      // Prepare terminate payload
      const payload = {
        roomId: sessionData.roomId,
      };

      // Execute terminate API call
      const terminateResponse = await RefreshableRequestHelper.requestKokatto(() => 
        inboundCallServiceClient.sendOutboundCallAnswer(payload, "terminate")
      );

      if (isKokattoErrorResponse(terminateResponse)) {
        throw new Error("Error terminating call");
      }

      console.log("Complete endCall logic successful for corrupted controller");
    } catch (error) {
      console.error("Error disconnecting call in adapter", error);
      throw new Error("Error disconnecting call " + error);
    } finally {
      // Clean up audio elements and webrtc if they exist in the corrupted controller
      const controllerData = this.outboundController as any;
      
      // Audio element cleanup
      if (controllerData.remoteAudioElement) {
        try {
          controllerData.remoteAudioElement.pause();
          controllerData.remoteAudioElement.srcObject = null;
          controllerData.remoteAudioElement.remove();
          controllerData.remoteAudioElement = null;
        } catch (audioError) {
          console.warn("Error cleaning up audio element:", audioError);
        }
      }

      // WebRTC cleanup
      if (controllerData.webrtc && typeof controllerData.webrtc.close === 'function') {
        try {
          controllerData.webrtc.close();
        } catch (webrtcError) {
          console.warn("Error closing WebRTC:", webrtcError);
        }
      }

      // Set final state
      if (controllerData.currentState !== undefined) {
        controllerData.currentState = OutboundCallState.ENDED;
      }
    }
  }

  /** Toggles call UI expansion state */
  toggleUIExpansion(): void {
    this._isExpanded = !this._isExpanded;
  }

  /** Get the underlying OutboundCallController instance */
  getOutboundController(): OutboundCallController {
    return this.outboundController;
  }

  /** Check if this is an outbound call adapter */
  isOutboundCall(): boolean {
    return true;
  }
}
